import { Settings } from '@streamr/tv-engine/~/settings';
import { Track } from './Track';
export class AppAudioDecoder {
    LOG_PREFIX = '[AppAudioDecoder]';
    audioDecoder;
    frameFifo = [];
    unencodedOutputTrack;
    done = false;
    configured = false;
    configuring = false;
    decodedFrameCount = 0;
    codec;
    pendingFrames = [];
    constructor(trackId, codec = Settings.audioCodec) {
        this.codec = codec;
        this.unencodedOutputTrack = new Track(trackId, false);
        console.log(`${this.LOG_PREFIX} constructor called`, {
            trackId,
            codec,
        });
    }
    async init(errorListener) {
        console.log(`${this.LOG_PREFIX} init called`);
        this.audioDecoder = new AudioDecoder({
            output: (audioData) => {
                const encodedAudioFrame = this.popFrameFromFifo();
                if (!encodedAudioFrame) {
                    console.error(`${this.LOG_PREFIX} No encoded audio frame found in fifo`);
                    audioData.close();
                    return;
                }
                const unencodedAudioFrame = this.createUnencodedAudioFrame(encodedAudioFrame, audioData);
                this.decodedFrameCount++;
                this.unencodedOutputTrack?.emit(unencodedAudioFrame, false, [
                    unencodedAudioFrame.data.buffer,
                ]);
                if (this.done && this.frameFifo.length === 0) {
                    this.shutdown().catch(console.error);
                }
            },
            error: (error) => {
                console.error(`${this.LOG_PREFIX} decoder error:`, error);
                errorListener(error);
            },
        });
        if (this.codec === 'opus') {
            await this.configureDecoder();
        }
        // AAC: defer configuration until the first frame with extradata arrives
        console.log(`${this.LOG_PREFIX} init completed`);
    }
    async configureDecoder(extradata) {
        const config = {
            codec: this.codec,
            sampleRate: Settings.audioSampleRate,
            numberOfChannels: Settings.audioNumberOfChannels,
            ...(extradata && { description: extradata }),
        };
        const support = await AudioDecoder.isConfigSupported(config);
        if (!support.supported) {
            throw new Error(`${this.LOG_PREFIX} Audio decoder config not supported: ${JSON.stringify(config)}`);
        }
        this.audioDecoder?.configure(config);
        this.configured = true;
        console.log(`${this.LOG_PREFIX} decoder configured`, {
            codec: this.codec,
            hasDescription: !!extradata,
        });
    }
    async destroy() {
        console.log(`${this.LOG_PREFIX} destroy called`);
        if (this.audioDecoder && this.audioDecoder.state !== 'closed') {
            await this.audioDecoder.flush();
            this.audioDecoder.close();
        }
        this.configured = false;
        this.configuring = false;
    }
    feedEncodedAudioFrame(frame) {
        if (frame.done) {
            console.log(`${this.LOG_PREFIX} done frame received`);
            if (this.frameFifo.length === 0) {
                this.shutdown().catch(console.error);
            }
            else {
                this.done = true;
            }
            return;
        }
        if (this.done) {
            console.warn(`${this.LOG_PREFIX} is done, cannot feed more frames`);
            return;
        }
        // For AAC: defer configuration until extradata (AudioSpecificConfig) arrives
        if (!this.configured) {
            if (!this.configuring &&
                frame.extradata &&
                frame.extradata.length > 0) {
                this.configuring = true;
                this.configureDecoder(frame.extradata)
                    .then(() => {
                    for (const pending of this.pendingFrames) {
                        this.decodeFrame(pending);
                    }
                    this.pendingFrames = [];
                    this.decodeFrame(frame);
                })
                    .catch(console.error);
            }
            else {
                this.pendingFrames.push(frame);
            }
            return;
        }
        this.decodeFrame(frame);
    }
    decodeFrame(frame) {
        if (!this.audioDecoder)
            return;
        try {
            this.pushFrameToFifo(frame);
            const encodedChunk = new EncodedAudioChunk({
                type: 'key',
                timestamp: Number(frame.timestamp),
                data: frame.data,
            });
            this.audioDecoder.decode(encodedChunk);
        }
        catch (error) {
            console.error(`${this.LOG_PREFIX} Error decoding frame:`, error, 'frame:', frame.frameNumber);
        }
    }
    async shutdown() {
        console.log(`${this.LOG_PREFIX} shutdown called`, {
            totalDecodedFrames: this.decodedFrameCount,
        });
        await this.destroy();
        this.unencodedOutputTrack?.emit(undefined, true);
        console.log(`${this.LOG_PREFIX} shutdown completed`);
    }
    pushFrameToFifo(frame) {
        this.frameFifo.push(frame);
    }
    popFrameFromFifo() {
        return this.frameFifo.shift();
    }
    createUnencodedAudioFrame(frame, audioData) {
        const samples = new Float32Array(audioData.numberOfFrames);
        audioData.copyTo(samples, { planeIndex: 0 });
        audioData.close();
        return {
            frameNumber: frame.frameNumber,
            timestamp: frame.timestamp,
            duration: frame.duration,
            trackId: frame.trackId,
            data: samples,
            done: frame.done,
            isKeyFrame: frame.isKeyFrame,
        };
    }
    getUnencodedOutputTrack() {
        return this.unencodedOutputTrack;
    }
}
